import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Sparkles } from "lucide-react";

const RESOURCE_LABEL = {
  customers: "customers", pets: "pets", staff: "staff members", branches: "branches",
  products: "products", services: "services", users: "team members",
  bookings_per_month: "bookings this month", memberships: "membership plans",
  packages: "packages", coupons: "coupons", automations: "automations",
};

const FEATURE_LABEL = {
  ai: "AI", shally: "Shally", loyalty: "Loyalty", marketing: "Marketing",
  multi_branch: "Multi-branch", memberships: "Memberships", packages: "Packages",
  workflows: "Workflows", journeys: "Journeys", opportunities: "Opportunities",
  intelligence: "Intelligence", recommendations: "Recommendations",
  predictions: "Predictions", shally_workflow_builder: "Shally Workflow Builder",
};

const PLAN_NAME = { free: "Free", starter: "Starter", professional: "Growth", business: "Multi-Branch", enterprise: "Enterprise" };
// next plan that unlocks more, by current plan (used for quantity limits)
const NEXT_PLAN = { free: "Starter", starter: "Growth", professional: "Multi-Branch", business: "Multi-Branch" };

// Globally mounted once (AppShell). Opens whenever any API call is blocked by a plan
// entitlement gate (402 PLAN_LIMIT_REACHED or 403 FEATURE_NOT_AVAILABLE). The server is the
// single source of truth; this is purely the upgrade experience.
export function PlanLimitDialog() {
  const [info, setInfo] = useState(null);
  const nav = useNavigate();
  useEffect(() => {
    const h = (e) => setInfo(e.detail);
    window.addEventListener("plan-limit-reached", h);
    return () => window.removeEventListener("plan-limit-reached", h);
  }, []);
  if (!info) return null;

  const isFeature = info.error === "FEATURE_NOT_AVAILABLE";
  const plan = info.plan || info.current_plan || "free";
  const planName = PLAN_NAME[plan] || plan;
  // features carry an authoritative required plan from the server; quantity limits use NEXT_PLAN
  const upgradeTo = isFeature
    ? (info.required_plan_name || PLAN_NAME[info.required_plan] || "Growth")
    : (NEXT_PLAN[plan] || "a higher");
  const resLabel = isFeature
    ? (FEATURE_LABEL[info.feature] || (info.feature || "this feature").replace(/_/g, " "))
    : (RESOURCE_LABEL[info.resource] || (info.resource || "").replace(/_/g, " "));

  return (
    <Dialog open onOpenChange={() => setInfo(null)}>
      <DialogContent className="bg-white max-w-md rounded-2xl" data-testid="plan-limit-dialog">
        <DialogHeader>
          <div className="mx-auto mb-2 grid h-12 w-12 place-items-center rounded-2xl bg-primary/10">
            <Sparkles className="h-6 w-6 text-primary" />
          </div>
          <DialogTitle className="text-center font-display text-xl">
            You're getting more out of Pawkt
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 text-sm text-pawkt-text-secondary" data-testid="plan-limit-body">
          <p className="text-center">
            {isFeature ? (
              <>The <span className="font-semibold text-pawkt-text">{resLabel}</span> feature isn't included in your <span className="font-semibold text-pawkt-text">{planName}</span> plan.</>
            ) : (
              <>Your <span className="font-semibold text-pawkt-text">{planName}</span> plan has reached its {resLabel} limit.</>
            )}
          </p>

          {!isFeature && typeof info.limit === "number" && (
            <div className="rounded-xl border border-[#EEE8DD] bg-[#FAF8F5] p-4 text-center" data-testid="plan-limit-usage">
              <div className="text-2xl font-extrabold text-pawkt-text">
                {info.current ?? info.limit} / {info.limit < 0 ? "∞" : info.limit}
              </div>
              <div className="mt-0.5 text-xs uppercase tracking-wide text-pawkt-text-secondary">{resLabel}</div>
            </div>
          )}

          <p className="text-center">
            {isFeature ? (
              <><span className="font-semibold text-pawkt-text">{resLabel}</span> is available on <span className="font-semibold text-primary">{upgradeTo}</span> and above.</>
            ) : (
              <>Upgrade to <span className="font-semibold text-primary">{upgradeTo}</span> to add more {resLabel}.</>
            )}
          </p>
        </div>

        <DialogFooter className="sm:justify-center gap-2">
          <Button variant="outline" onClick={() => setInfo(null)} data-testid="plan-limit-notnow">Not now</Button>
          <Button className="bg-primary text-white hover:bg-primary/90" data-testid="plan-limit-upgrade"
            onClick={() => { setInfo(null); nav("/billing#plans"); }}>
            Upgrade Plan
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
